import type { AccountRole } from "../integrations/supabase/domain";

/**
 * Last resolved role of the signed-in account, persisted so the shell can pick the right nav on
 * the first paint of a cold load instead of defaulting to the tracking nav while the owner context
 * resolves. It is only a hint: it is ignored unless it was written for the exact user id of the
 * current persisted session, cleared on sign-out, and overwritten every time the live context
 * resolves. It never decides access — route gates still redirect on the live context.
 */
export const ACTOR_ROLE_HINT_KEY = "nfp.actorRoleHint";

export type ActorRoleHint = { userId: string; role: AccountRole };

const isAccountRole = (value: unknown): value is AccountRole =>
  value === "tracking" || value === "viewing" || value === "instructor";

export const readActorRoleHint = (userId: string | null): AccountRole | null => {
  if (!userId) return null;

  try {
    const raw = window.localStorage.getItem(ACTOR_ROLE_HINT_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<ActorRoleHint> | null;
    if (!parsed || parsed.userId !== userId || !isAccountRole(parsed.role)) {
      return null;
    }

    return parsed.role;
  } catch {
    return null;
  }
};

export const writeActorRoleHint = (hint: ActorRoleHint) => {
  try {
    window.localStorage.setItem(ACTOR_ROLE_HINT_KEY, JSON.stringify(hint));
  } catch {
    /* storage blocked: the shell just falls back to the neutral nav while loading */
  }
};

export const clearActorRoleHint = () => {
  try {
    window.localStorage.removeItem(ACTOR_ROLE_HINT_KEY);
  } catch {
    /* ignore storage failures */
  }
};
